import React, { useCallback, useState, useMemo } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { Customer, DropTitle, FormButton, InvoiceType, ProfileDropdown, RadioWrapper, TopSection } from './styles';
import Button from '../../button';
import {
  BUTTON_TYPE,
  CREATE_INVOICE_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  invoiceTypeOptions
} from '../../../global/constants';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import { getProjectHours } from '../../../services/invoiceService';
import { ROUTES } from '../../../routes/urls';
import { useHistory } from 'react-router-dom';
import Modal from '../modal';
import CustomerModal from '../customerModal';
import { captureException } from '../../../services/logService';
import { isEmpty } from '../../../helpers/common';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { INVOICE_ANALYTICS } from '../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../dropdown';
import RadioButton from '../../radioButton';
import { dispatchSettingError } from '../../../services/settingServices';
import IconButton from '../../button/iconButton';
import Input from '../../input';
import { useMobileDevice } from '../../../global/useMobile';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

function AddInvoiceModal({
  closeModel,
  isCustomerDetail,
  customerDetail,
  CustomerInvoiceModal
}: {
  closeModel: () => void;
  isCustomerDetail?: boolean;
  customerDetail?: { id: string; companyName: string };
  CustomerInvoiceModal?: boolean;
}) {
  //States
  const [customer, setCustomer] = React.useState({
    id: customerDetail?.id || '',
    companyName: customerDetail?.companyName || undefined
  });
  const [open, setOpen] = useState(false);
  const [value, setValue] = React.useState('');
  const [nextLoading, setNextLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Mobx variables
  const { customerStore } = rootStore;
  const { customers } = customerStore;

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
  }, [customers]);

  //Other variable
  const history = useHistory();
  const isMobile = useMobileDevice();

  const onUpdateInputValue = useCallback((value: string, obj: CustomerDetailsInterface) => {
    setCustomer(obj);
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleChange = (value: string) => {
    setValue(value);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onButtonClick = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setNextLoading(true);
        if (isEmpty(customer?.id)) {
          dispatchSettingError('Please select customer!');
          return;
        }
        if (value === CREATE_INVOICE_TYPE.TRACKED_TIME_AND_EXPENSE && !isEmpty(customer?.id)) {
          const result = await getProjectHours(customer?.id);
          if (result > 0) {
            trackAnalyticActivity(INVOICE_ANALYTICS.VIEW_TIMELOG);
            history.push(`${ROUTES.ADD_NEW_INVOICES}?customer=${customer?.id}`);
          } else {
            history.push(`${ROUTES.ADD_NEW_INVOICES_2}?customer=${customer?.id}`);
          }
        } else if (value === CREATE_INVOICE_TYPE.BLANK_INVOICE && !isEmpty(customer?.id)) {
          history.push(`${ROUTES.ADD_NEW_INVOICES_2}?customer=${customer?.id}`);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setNextLoading(false);
      }
    },
    [value, history, customer]
  );
  return (
    <>
      <ProfileDropdown>
        <TopSection>
          <DropTitle>Customer</DropTitle>
          <Customer>
            <Dropdown
              modalTitle='Customer'
              isMinWidth={CustomerInvoiceModal ? 339 : 294}
              dropdownContentWidth='100%'
              updateDropdownOpen={(value) => setIsDropdownOpen(value)}
              content={
                <Input
                  value={customer?.companyName || 'Select a customer'}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                  readOnly={true}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                />
              }
              trigger='click'>
              {sortedCustomers.map((item) => {
                const displayName = item.companyName || 'Unknown';
                const isSelected = customer?.companyName === item.companyName;
                return (
                  <DropdownItem
                    key={item.value}
                    onClick={() => onUpdateInputValue(item.value ?? '', item)}
                    label={displayName}
                    isSuffix={
                      isSelected ? (
                        <SVGIcon
                          name='checkbox-icon'
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                        />
                      ) : null
                    }
                  />
                );
              })}
            </Dropdown>
            {!isCustomerDetail && (
              <IconButton
                iconName='plus-icon'
                size={isMobile ? ICON_BUTTON_SIZE.EXTRA_X_LARGE : ICON_BUTTON_SIZE.EXTRA_LARGE}
                variant={ICON_BUTTON_TYPE.SECONDARY}
                onClick={handleOpen}
              />
            )}
          </Customer>
        </TopSection>
        <InvoiceType>
          <DropTitle>Invoice Type</DropTitle>
          <RadioWrapper>
            {invoiceTypeOptions.map((option) => (
              <RadioButton
                key={option.id}
                className='radio-Button'
                id={option.id}
                name='invoiceType'
                value={option.value}
                checked={value === option.value}
                label={option.label}
                onChange={(value: string | number) => handleChange(value as string)}
              />
            ))}
          </RadioWrapper>
        </InvoiceType>
        <FormButton>
          <Button
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => onButtonClick(e)}
            variant={BUTTON_TYPE.PRIMARY}
            title={'Next Step'}
            isLoading={nextLoading}
          />
          <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={closeModel} buttonType='button' />
        </FormButton>
      </ProfileDropdown>
      <Modal open={open} onClose={handleClose} maxWidth='692px' isResponsiveMaxWidth={true}>
        <CustomerModal handleCloseModal={handleClose} />
      </Modal>
    </>
  );
}

export default observer(AddInvoiceModal);
